import { FC, useMemo } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Box, Button, Menu, Portal } from '@chakra-ui/react';
import { AccessorKeyColumnDef } from '@tanstack/react-table';

import { useApplications } from '~/core/shared/api/middle-office/application';
import { EMPTY_ARR } from '~/core/shared/constants';
import { useTableParams } from '~/core/shared/hooks';
import { AnyDocument, Sorting } from '~/core/shared/types';
import { createFilterParams } from '~/core/shared/utils/queries';
import { FilterListType } from '../../components/settings/DataLists/types';
import { ReactQueryTable } from './ReactQueryTable.component';

type DataListTableProps = {
  canUpdate?: boolean;
  listType: FilterListType;
  search?: string;
  selectedList: string;
  columns: AccessorKeyColumnDef<AnyDocument>[];
  onEdit: (documentId: string, data: Record<string, any>) => void;
  onDelete: (documentId: string) => void;
  valueLabel: string;
};

const getOptionsColumn = (
  columns: DataListTableProps['columns'],
  onEdit: DataListTableProps['onEdit'],
  onDelete: DataListTableProps['onDelete'],
): AccessorKeyColumnDef<AnyDocument>[] => {
  const editableColumns = columns
    .map((column) => column.accessorKey)
    .filter((column) => !['updatedAt', 'createdAt'].includes(column as string));

  return [
    {
      accessorKey: 'id',
      enableSorting: false,
      cell({ row }) {
        const editableValues = Object.keys(row.original).reduce(
          (acc: { [key: string]: any }, key) => {
            if (editableColumns.includes(key)) {
              acc[key] = row.original[key];
            }
            return acc;
          },
          {},
        );

        return (
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button
                colorPalette="gray"
                h="32px"
                minW="inherit"
                p={0}
                textAlign="center"
                w="32px"
              >
                <BsThreeDotsVertical />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content
                  borderColor="gray.300"
                  borderRadius={0}
                  borderWidth="1px"
                  minW="inherit"
                  w="127px"
                >
                  <Menu.Item
                    color="black"
                    fontSize="16px"
                    onClick={() => {
                      onEdit(row.original.id, editableValues);
                    }}
                    value="edit"
                  >
                    Edit
                  </Menu.Item>

                  <Menu.Item
                    color="black"
                    fontSize="16px"
                    onClick={() => onDelete(row.original.id)}
                    value="delete"
                  >
                    Delete
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        );
      },
      header: '',
    },
  ];
};

const defaultSorting: Sorting = { column: 'created_at', direction: 'desc' };

export const DataListTable: FC<DataListTableProps> = ({
  canUpdate,
  listType,
  search,
  selectedList,
  columns,
  onEdit,
  onDelete,
  valueLabel,
}) => {
  const additionalParams = useMemo(
    () =>
      createFilterParams({
        document_type_category: selectedList,
        ...(search ? { search } : undefined),
        ...(listType ? { type: listType } : undefined),
      }),
    [selectedList, search, listType],
  );

  const { tableProps, searchParams } = useTableParams(
    defaultSorting,
    10,
    additionalParams,
  );

  const { applications, meta, ...applicationsQueryState } = useApplications(
    searchParams,
    { definitionName: selectedList },
  );

  const optionsColumn = useMemo(
    () => (canUpdate ? getOptionsColumn(columns, onEdit, onDelete) : EMPTY_ARR),
    [canUpdate, columns, onDelete, onEdit],
  );

  const allColumns = useMemo(
    () =>
      [...columns, ...optionsColumn].map((column) => {
        if (column.accessorKey !== 'value') return column;

        return { ...column, header: valueLabel };
      }),
    [columns, optionsColumn, valueLabel],
  );

  return (
    <Box bg="white">
      <ReactQueryTable<AnyDocument>
        columns={allColumns}
        data={applications as AnyDocument[]}
        isLoading={applicationsQueryState.isFetching}
        meta={meta}
        {...tableProps}
      />
    </Box>
  );
};
